import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Skeleton, Space, Typography, message } from "antd";
import { SafetyCertificateOutlined, ArrowLeftOutlined, ReloadOutlined } from "@ant-design/icons";
import { mfaApi, apiErrorMessage, MfaSetupData } from "../../api/auth/authService";
import OtpInput from "../../components/ui/OtpInput";

/**
 * Second step of sign-in. Two cases, one code box:
 *  - enrolled user: enter the code from the authenticator app
 *  - setupRequired (enforced): scan the QR (or type the key) first, then the code switches MFA on
 * The ticket (mfaToken) lives in React state only: a refresh goes back to the password step.
 */
const MfaLoginStep: React.FC<{
  mfaToken: string;
  setupRequired: boolean;
  email?: string;
  onSuccess: (mfaJustEnabled: boolean) => Promise<void> | void;
  onBack: () => void;
}> = ({ mfaToken, setupRequired, email, onSuccess, onBack }) => {
  const [setup, setSetup] = useState<MfaSetupData | null>(null);
  const [showKey, setShowKey] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const inflight = useRef<Promise<MfaSetupData> | null>(null);

  // de-duplicate the setup call (StrictMode runs effects twice in dev; two secrets would show two QRs)
  const loadSetup = (regenerate = false) => {
    if (inflight.current && !regenerate) return inflight.current;
    const p = mfaApi.loginSetup(mfaToken, regenerate).then((d) => { setSetup(d); return d; })
      .catch((e) => { setErr(apiErrorMessage(e, "Could not start the set-up.")); throw e; })
      .finally(() => { inflight.current = null; });
    inflight.current = p;
    return p;
  };
  useEffect(() => { if (setupRequired) loadSetup().catch(() => undefined); }, [setupRequired]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (v: string) => {
    if (busy) return;
    setBusy(true); setErr(null);
    try {
      const r = await mfaApi.loginVerify(mfaToken, v);
      await onSuccess(!!r.mfaJustEnabled);
    } catch (e: any) {
      const status = e?.response?.status;
      setErr(apiErrorMessage(e, "That code was not accepted."));
      setCode("");
      if (status === 401) message.warning("Your sign-in session expired. Please sign in again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 8 }}>
      <Space align="center" style={{ marginBottom: 12 }}>
        <SafetyCertificateOutlined style={{ fontSize: 22, color: "#2263b3" }} />
        <Typography.Title level={4} style={{ margin: 0 }}>{setupRequired ? "Set up two-step verification" : "Two-step verification"}</Typography.Title>
      </Space>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
        {setupRequired
          ? <>Your organisation requires an authenticator app. Scan the code with Google Authenticator, Microsoft Authenticator or any TOTP app, then enter the 6-digit code it shows.</>
          : <>Enter the 6-digit code from your authenticator app{email ? <> for <strong>{email}</strong></> : null}.</>}
      </Typography.Paragraph>

      {setupRequired && (
        <div style={{ display: "flex", gap: 20, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 16 }}>
          {setup
            ? <img src={setup.qrCodeDataUrl} alt="Scan this QR code with your authenticator app" width={176} height={176} style={{ border: "1px solid #e5e9f2", borderRadius: 8, background: "#fff", padding: 6 }} />
            : <Skeleton.Node active style={{ width: 176, height: 176 }}><span /></Skeleton.Node>}
          <div style={{ flex: 1, minWidth: 220, fontSize: 13 }}>
            <ol style={{ paddingLeft: 18, margin: 0, color: "#4b5563" }}>
              <li>Open your authenticator app and choose <em>Add account</em>.</li>
              <li>Scan the QR code.</li>
              <li>Enter the 6-digit code below.</li>
            </ol>
            <div style={{ marginTop: 10 }}>
              <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setShowKey((s) => !s)}>{showKey ? "Hide the key" : "Can't scan the code?"}</Button>
              {showKey && setup && (
                <div style={{ marginTop: 6 }}>
                  <div style={{ color: "#6b7280" }}>Enter this key manually (time-based, 6 digits, 30 s):</div>
                  <code style={{ display: "inline-block", marginTop: 4, padding: "4px 8px", background: "#f3f4f6", borderRadius: 6, letterSpacing: 2, wordBreak: "break-all" }}>
                    {setup.secret.replace(/(.{4})/g, "$1 ").trim()}
                  </code>
                </div>
              )}
              <div><Button type="link" size="small" icon={<ReloadOutlined />} style={{ padding: 0 }} onClick={() => loadSetup(true).catch(() => undefined)}>Get a new QR code</Button></div>
            </div>
          </div>
        </div>
      )}

      {err && <Alert type="error" showIcon message={err} style={{ marginBottom: 12 }} />}

      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <OtpInput value={code} onChange={setCode} onComplete={submit} disabled={busy} />
        <Button type="primary" size="large" loading={busy} disabled={code.length !== 6} onClick={() => submit(code)}>Verify</Button>
      </div>
      <div style={{ marginTop: 14, fontSize: 12, color: "#6b7280" }}>
        Lost your device? Contact your administrator to reset two-step verification.
      </div>
      <Button type="link" icon={<ArrowLeftOutlined />} style={{ padding: 0, marginTop: 10 }} onClick={onBack}>Back to sign in</Button>
    </div>
  );
};

export default MfaLoginStep;
